import { ChevronRight, StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { NoticeMark } from 'common-src/components/base';

import { pathwayLabel } from '../../constants';
import {
  dayName,
  daysUntil,
  formatClock12,
  monthDay,
  parseReadinessCalendarDate,
} from '../../helpers';
import { Badge } from '../Badge';
import ProviderAvatars from '../ProviderAvatars';
import {
  clinicVisitTimes,
  SmallCaps,
  visitCode,
  VisitCodeBadge,
  visitLabelWithoutCode,
} from '../Visit';
import styles from './MemberGroup.module.scss';

export const useNextVisitByPatientId = (appointments) =>
  useMemo(() => {
    const nextByPatient = {};
    appointments.forEach((appointment) => {
      const patientId = `${appointment?.patientId ?? ''}`;
      const times = clinicVisitTimes(appointment);
      const day = parseReadinessCalendarDate(times.date);
      if (!patientId || !day || daysUntil(times.date) < 0) return;
      const current = nextByPatient[patientId];
      if (
        current &&
        `${current.times.date} ${current.times.start}` <= `${times.date} ${times.start}`
      ) {
        return;
      }
      nextByPatient[patientId] = {
        code: visitCode({ cptCode: appointment.cptCode, visitType: appointment.visitType }),
        kindName: appointment.visitType,
        label: visitLabelWithoutCode(appointment.visitType),
        when: `${dayName(day)}, ${monthDay(day)} · ${[formatClock12(times.start), times.zone]
          .filter(Boolean)
          .join(' ')}`,
        times,
      };
    });
    return nextByPatient;
  }, [appointments]);

export const splitMemberTasks = (group) => [
  {
    key: 'overdue',
    label: 'Overdue',
    source: group.priorityTasks.filter((task) => daysUntil(task.due) < 0),
  },
  {
    key: 'priority',
    label: 'Priority',
    source: group.priorityTasks.filter((task) => !(daysUntil(task.due) < 0)),
  },
  { key: 'future', label: 'Future', source: group.futureTasks },
];

const getUrgency = (group) => {
  if (group.criticalCount) return 'now';
  if (group.blockedCount) return 'held';
  if (group.escalatedCount) return 'escalated';
  const soonestDays = Number.isFinite(group.nextDue)
    ? Math.floor((group.nextDue - Date.now()) / 86400000)
    : Infinity;
  if (soonestDays <= 0) return 'now';
  return soonestDays <= 2 ? 'soon' : 'later';
};

const getRowLabel = (group, nextVisit, noteCount) =>
  [
    group.patientName,
    `${group.tasks.length} ${group.tasks.length === 1 ? 'task' : 'tasks'}`,
    group.criticalCount ? `${group.criticalCount} critical` : null,
    group.blockedCount ? `${group.blockedCount} blocked` : null,
    group.escalatedCount ? `${group.escalatedCount} escalated` : null,
    `soonest due ${`${group.nextDueLabel}`.toLowerCase()}`,
    nextVisit ? `next visit ${nextVisit.label}, ${nextVisit.when}` : 'no visit booked',
    noteCount ? `${noteCount} huddle ${noteCount === 1 ? 'note' : 'notes'}` : null,
  ]
    .filter(Boolean)
    .join(', ');

export const MemberGroupHeader = () => (
  <div className={styles.memberHeader}>
    <span className={styles.memberLead} />
    <SmallCaps className={styles.memberHeaderMember}>Member</SmallCaps>
    <SmallCaps className={`${styles.hairlineCell} ${styles.visitCell}`}>Next visit</SmallCaps>
    <SmallCaps className={`${styles.hairlineCell} ${styles.teamCell}`}>Care team</SmallCaps>
    <SmallCaps className={`${styles.hairlineCell} ${styles.tasksCell}`}>Open tasks</SmallCaps>
    <SmallCaps className={`${styles.hairlineCell} ${styles.dueColumn}`}>Due</SmallCaps>
  </div>
);

export const MemberGroupRow = ({ group, expanded, onToggle, nextVisit, noteCount }) => {
  const pathway = pathwayLabel[group.pathway] ?? group.pathway;
  const urgency = getUrgency(group);
  const dueUrgent = /^Today$|overdue$/.test(`${group.nextDueLabel}`);
  const toggleOnKey = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={getRowLabel(group, nextVisit, noteCount)}
      className={`${styles.memberRow} ${expanded ? styles.memberRowOpen : ''}`}
      onClick={onToggle}
      onKeyDown={toggleOnKey}
    >
      <span className={styles.memberLead}>
        <ChevronRight
          size={14}
          aria-hidden="true"
          className={`${styles.memberChevron} ${expanded ? styles.memberChevronOpen : ''}`}
        />
        <span
          className={`${styles.urgencyBar} ${styles[`urgency${urgency}`]}`}
          aria-hidden="true"
        />
      </span>
      <div className={styles.memberCell}>
        <div className={styles.memberNameLine}>
          <span className={styles.memberName}>{group.patientName}</span>
          {group.patientMrn ? <span className={styles.memberMrn}>{group.patientMrn}</span> : null}
          {noteCount > 0 && (
            <NoticeMark
              tone="neutral"
              icon={StickyNote}
              count={noteCount}
              label={`${noteCount} huddle note${noteCount === 1 ? '' : 's'}`}
            />
          )}
        </div>
        <div className={styles.memberPhase}>
          {group.patientPhase != null ? `Phase ${group.patientPhase}` : null}
          {group.patientPhase != null && pathway ? ' · ' : null}
          {pathway}
        </div>
      </div>
      <div className={`${styles.hairlineCell} ${styles.visitCell}`}>
        {nextVisit ? (
          <span className={styles.visitLine}>
            <span className={styles.visitMark}>
              <VisitCodeBadge code={nextVisit.code} visitType={nextVisit.kindName} size="sm" />
            </span>
            <span className={styles.visitName} title={nextVisit.label}>
              {nextVisit.label}
            </span>
            <span className={styles.visitWhen}>{nextVisit.when}</span>
          </span>
        ) : (
          <span className={styles.noVisit}>No visit booked</span>
        )}
      </div>
      <div className={`${styles.hairlineCell} ${styles.teamCell}`}>
        {group.patientClinician ? (
          <ProviderAvatars name={group.patientClinician} jobTitle="Provider" />
        ) : null}
      </div>
      <div className={`${styles.hairlineCell} ${styles.tasksCell}`}>
        <div className={styles.countBadges}>
          {group.criticalCount ? (
            <Badge variant="critical" className={styles.countBadge}>
              {group.criticalCount} critical
            </Badge>
          ) : null}
          {group.blockedCount ? (
            <Badge variant="critical" className={styles.countBadge}>
              {group.blockedCount} blocked
            </Badge>
          ) : null}
          {group.escalatedCount ? (
            <Badge variant="escalated" className={styles.countBadge}>
              {group.escalatedCount} escalated
            </Badge>
          ) : null}
          <span className={styles.nowLater}>
            <span className={`${styles.nowPart} ${group.nowCount ? styles.nowPartOn : ''}`}>
              <span className={styles.nowNumber}>{group.nowCount}</span>
              <span className={styles.nowWord}>now</span>
            </span>
            {group.laterCount ? (
              <span className={styles.laterPart}>
                <span className={styles.laterNumber}>{group.laterCount}</span>
                <span className={styles.laterWord}>later</span>
              </span>
            ) : null}
          </span>
        </div>
      </div>
      <span
        className={`${styles.hairlineCell} ${styles.dueColumn} ${
          dueUrgent ? styles.dueColumnUrgent : ''
        }`}
      >
        {group.nextDueLabel}
      </span>
    </div>
  );
};

export const BucketHeader = ({ bucketKey, label, count }) => (
  <div className={styles.bucketHeader}>
    <span className={`${styles.bucketIndex} ${styles[`bucketIndex${bucketKey}`]}`}>
      {bucketKey === 'overdue' ? '!' : bucketKey === 'priority' ? '1' : '2'}
    </span>
    <SmallCaps className={styles[`bucketLabel${bucketKey}`]}>{label}</SmallCaps>
    <span className={styles.bucketCount}>{count}</span>
  </div>
);

MemberGroupRow.propTypes = {
  group: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  nextVisit: PropTypes.object,
  noteCount: PropTypes.number,
};

BucketHeader.propTypes = {
  bucketKey: PropTypes.oneOf(['overdue', 'priority', 'future']).isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};
